import { useState, useRef, useEffect } from 'react'

/**
 * Agent API helpers for connecting to Cloudflare Durable Object agents
 * via the `agents` library WebSocket interface.
 */

// ─── Constants ───────────────────────────────────────────────────────────────

/** Known agent class names registered on the backend */
export const AGENT_NAMES = {
  ChatAgent: 'ChatAgent',
  OrchestratorAgent: 'OrchestratorAgent',
  CoderAgent: 'CoderAgent',
  DeployerAgent: 'DeployerAgent',
  ReviewerAgent: 'ReviewerAgent',
} as const

export type AgentName = (typeof AGENT_NAMES)[keyof typeof AGENT_NAMES]

/** Default instance name used when no specific instance is requested */
export const DEFAULT_INSTANCE = 'default'

// ─── Reconnection Config ─────────────────────────────────────────────────────

export interface ReconnectConfig {
  maxRetries?: number
  retryDelayMs?: number
  backoffMultiplier?: number
  onReconnect?: (attempt: number) => void
  onError?: (error: Error) => void
}

const DEFAULT_RECONNECT_CONFIG: Required<ReconnectConfig> = {
  maxRetries: 5,
  retryDelayMs: 1000,
  backoffMultiplier: 1.5,
  onReconnect: () => {},
  onError: (err) => console.warn('Agent connection error:', err),
}

// ─── Agent Client Factory ────────────────────────────────────────────────────

/**
 * Dynamically import and return an `AgentClient` for the given agent.
 */
export async function createAgentClient(
  agentName: string,
  instanceName: string = DEFAULT_INSTANCE
) {
  const { AgentClient } = await import('agents/client')
  return new AgentClient({
    host: window.location.host,
    agent: agentName,
    name: instanceName,
  })
}

// ─── Reconnecting Agent Client ──────────────────────────────────────────────

/**
 * Creates an agent client with automatic reconnection on disconnect.
 * Uses the agents library's ready state to detect connection health.
 */
export function createReconnectingAgent(
  agentName: string,
  instanceName: string = DEFAULT_INSTANCE,
  config: ReconnectConfig = {}
) {
  const cfg = { ...DEFAULT_RECONNECT_CONFIG, ...config }
  
  let client: Awaited<ReturnType<typeof createAgentClient>> | null = null
  let reconnectTimer: ReturnType<typeof setTimeout> | null = null
  let healthCheckTimer: ReturnType<typeof setInterval> | null = null
  let retryCount = 0
  let isDisconnected = false
  
  const stateListeners = new Set<(state: 'connecting' | 'connected' | 'disconnected' | 'error') => void>()
  
  const notifyState = (state: 'connecting' | 'connected' | 'disconnected' | 'error') => {
    stateListeners.forEach(cb => cb(state))
  }
  
  const onStateChange = (cb: (state: 'connecting' | 'connected' | 'disconnected' | 'error') => void) => {
    stateListeners.add(cb)
    return () => stateListeners.delete(cb)
  }
  
  const calculateDelay = (attempt: number): number => {
    return Math.min(
      cfg.retryDelayMs * Math.pow(cfg.backoffMultiplier, attempt - 1),
      30000
    )
  }
  
  const startHealthCheck = () => {
    healthCheckTimer = setInterval(() => {
      // Check if client is still ready (library handles internal reconnection)
      if (client && !client.ready && !isDisconnected) {
        scheduleReconnect()
      }
    }, 5000)
  }
  
  const stopHealthCheck = () => {
    if (healthCheckTimer) {
      clearInterval(healthCheckTimer)
      healthCheckTimer = null
    }
  }
  
  const connect = async (): Promise<void> => {
    if (isDisconnected) return
    if (client?.ready) {
      notifyState('connected')
      return
    }
    
    notifyState('connecting')
    
    try {
      client = await createAgentClient(agentName, instanceName)
      await client.ready
      
      retryCount = 0
      startHealthCheck()
      notifyState('connected')
      cfg.onReconnect(0)
      
    } catch (error) {
      notifyState('error')
      cfg.onError(error instanceof Error ? error : new Error('Connection failed'))
      scheduleReconnect()
    }
  }
  
  const scheduleReconnect = (): void => {
    stopHealthCheck()
    
    if (retryCount >= cfg.maxRetries || isDisconnected) {
      notifyState('disconnected')
      return
    }
    
    retryCount++
    const delay = calculateDelay(retryCount)
    
    reconnectTimer = setTimeout(async () => {
      cfg.onReconnect(retryCount)
      await connect()
    }, delay)
  }
  
  const disconnect = (): void => {
    isDisconnected = true
    stopHealthCheck()
    if (reconnectTimer) {
      clearTimeout(reconnectTimer)
      reconnectTimer = null
    }
    // Note: agents library handles WebSocket cleanup on client GC
    client = null
    notifyState('disconnected')
  }
  
  const send = (message: string | object): boolean => {
    if (!client?.ready) {
      console.warn('Agent not ready, message dropped')
      return false
    }
    try {
      // Use the public send API from agents/client
      if (typeof client.send === 'function') {
        client.send(typeof message === 'string' ? message : JSON.stringify(message))
        return true
      }
      return false
    } catch (error) {
      cfg.onError(error instanceof Error ? error : new Error('Send failed'))
      return false
    }
  }
  
  const getState = () => ({
    ready: client?.ready ?? false,
    retryCount,
    isDisconnected,
  })
  
  return {
    connect,
    disconnect,
    send,
    onStateChange,
    getState,
  }
}

// ─── React Hook Wrapper ─────────────────────────────────────────────────────

/**
 * React hook for using a reconnecting agent client.
 */
export function useReconnectingAgent(
  agentName: string,
  instanceName: string = DEFAULT_INSTANCE,
  config?: ReconnectConfig
) {
  const [connectionState, setConnectionState] = useState<'connecting' | 'connected' | 'disconnected' | 'error'>('connecting')
  const controllerRef = useRef<ReturnType<typeof createReconnectingAgent> | null>(null)
  
  useEffect(() => {
    const controller = createReconnectingAgent(agentName, instanceName, config)
    controllerRef.current = controller
    
    const unsubscribe = controller.onStateChange(setConnectionState)
    controller.connect()
    
    return () => {
      unsubscribe()
      controller.disconnect()
    }
  }, [agentName, instanceName])
  
  return {
    state: connectionState,
    ready: connectionState === 'connected',
    send: controllerRef.current?.send ?? (() => false),
    connect: () => controllerRef.current?.connect(),
    disconnect: () => controllerRef.current?.disconnect(),
  }
}
